import React from 'react';
import PropTypes from 'prop-types';
import { Calendar, TimePickerView } from '@material-ui/pickers';
import Provider from '../Provider/Provider';
import { isAM, isPM } from './utils/time';

import DateTimePickerProvider from '../DateTimePickerProvider/DateTimePickerProvider';
import {
  Container,
  TimeContainer,
  Tabs,
  Tab,
  DateTimePickerTheme,
  CalendarContainer,
  YearsContainer,
} from './DateTimePicker.styled';
import YearSelector from './components/YearSelector';
import Header from './components/Header';
import * as VIEWS from './constants/views';

class DateTimePicker extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      openView: VIEWS.CALENDAR,
    };

    this.handleChangeView = this.handleChangeView.bind(this);
    this.handleAMClick = this.handleAMClick.bind(this);
    this.handlePMClick = this.handlePMClick.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleYearChange = this.handleYearChange.bind(this);
    this.handleCalendarChange = this.handleCalendarChange.bind(this);
    this.handleHourChange = this.handleHourChange.bind(this);
    this.handleMinutesChange = this.handleMinutesChange.bind(this);
    this.handleOpenViewChange = this.handleOpenViewChange.bind(this);
  }

  getTabValue() {
    const { openView } = this.state;
    return VIEWS.DATE_TAB.includes(openView)
      ? VIEWS.DATE_VIEW_INDEX
      : VIEWS.TIME_VIEW_INDEX;
  }

  handleChangeView(view) {
    this.setState({ openView: view });
  }

  handleAMClick() {
    const { date, onChange } = this.props;

    if (isPM(date)) {
      const hours = date.getHours();
      const newDate = new Date(date);
      newDate.setHours(hours - 12);
      onChange(newDate);
    }
  }

  handlePMClick() {
    const { date, onChange } = this.props;

    if (isAM(date)) {
      const hours = date.getHours();
      const newDate = new Date(date);
      newDate.setHours(hours + 12);
      onChange(newDate);
    }
  }

  handleTabChange(_event, value) {
    this.setState({
      openView:
        value === VIEWS.DATE_VIEW_INDEX ? VIEWS.DATE_TAB[0] : VIEWS.TIME_TAB[0],
    });
  }

  handleYearChange(newDate) {
    const { onChange } = this.props;
    onChange(newDate);
    this.setState({ openView: VIEWS.CALENDAR });
  }

  handleCalendarChange(newDate, isFinished) {
    const { onChange, dateOnly } = this.props;

    onChange(newDate);

    if (isFinished && !dateOnly) {
      this.setState({ openView: VIEWS.HOURS });
    }
  }

  handleHourChange(newDate, isFinished) {
    const { onChange, date } = this.props;
    if (isAM(newDate) && isPM(date)) {
      const hours = newDate.getHours();

      newDate.setHours(hours + 12);
    }
    onChange(newDate);

    if (isFinished) {
      this.setState({ openView: VIEWS.MINUTES });
    }
  }

  handleMinutesChange(newDate) {
    const { onChange } = this.props;
    onChange(newDate);
  }

  handleOpenViewChange(view) {
    this.setState({ openView: view });
  }

  renderHeader() {
    const { date, dateOnly } = this.props;
    const { openView } = this.state;
    return (
      <Header
        date={date}
        dateOnly={dateOnly}
        openView={openView}
        onViewChange={this.handleChangeView}
        onAMClicked={this.handleAMClick}
        onPMClicked={this.handlePMClick}
      />
    );
  }

  renderTabs() {
    const { dateOnly } = this.props;

    if (dateOnly) {
      // We are only rendering one selector so don't need tabs
      return null;
    }

    const value = this.getTabValue();

    return (
      <Tabs data-testid="tabs" value={value} onChange={this.handleTabChange}>
        <Tab label="Date" />
        <Tab label="Time" />
      </Tabs>
    );
  }

  renderOpenView() {
    const { openView } = this.state;
    const { date, disablePast, disableFuture, minDate, maxDate } = this.props;
    switch (openView) {
      case VIEWS.YEARS:
        return (
          <YearsContainer data-testid="year-selector">
            <YearSelector
              date={date}
              onChange={this.handleYearChange}
              disablePast={disablePast}
              disableFuture={disableFuture}
              minDate={minDate}
              maxDate={maxDate}
            />
          </YearsContainer>
        );
      case VIEWS.CALENDAR:
        return (
          <CalendarContainer data-testid="calendar-selector">
            <Calendar
              date={date}
              onChange={this.handleCalendarChange}
              disablePast={disablePast}
              disableFuture={disableFuture}
              minDate={minDate}
              maxDate={maxDate}
            />
          </CalendarContainer>
        );
      case VIEWS.HOURS:
      case VIEWS.MINUTES:
        return (
          <TimeContainer data-testid="time-selector">
            <TimePickerView
              date={date}
              type={openView}
              onHourChange={this.handleHourChange}
              onMinutesChange={this.handleMinutesChange}
              onSecondsChange={() => {}}
            />
          </TimeContainer>
        );
      default:
        return null;
    }
  }

  render() {
    return (
      <DateTimePickerProvider>
        <Provider theme={DateTimePickerTheme}>
          <Container data-testid="date-time-picker">
            {this.renderHeader()}
            {this.renderTabs()}
            {this.renderOpenView()}
          </Container>
        </Provider>
      </DateTimePickerProvider>
    );
  }
}

DateTimePicker.propTypes = {
  /** Default selected date */
  date: PropTypes.instanceOf(Date).isRequired,

  /** On change callback */
  onChange: PropTypes.func.isRequired,

  /** Disable past dates selections */
  disablePast: PropTypes.bool,

  /** Disable future dates selections */
  disableFuture: PropTypes.bool,

  /** Minimum selectable date */
  minDate: PropTypes.string,

  /** Maximum selectable date */
  maxDate: PropTypes.string,

  /** Date-only selection */
  dateOnly: PropTypes.bool,
};

DateTimePicker.defaultProps = {
  disablePast: false,
  disableFuture: false,
  minDate: undefined,
  maxDate: undefined,
  dateOnly: false,
};

export default DateTimePicker;
